import axios from 'axios'
import { clinicApiURL, commonHeadersCPWeb } from 'src/clinic/services'
import { addOrUpdatePatientsInIdb, searchPatientsInIdb } from 'src/emr/database/indexedDbDao'

/**
 * Lightweight patient search loader shared by mentor Patients and booking forms.
 *
 * Keep this module independent from ClinicManageBookingComponents: importing that component barrel
 * evaluates the consumer booking surface and its payment dependencies even when only patient search
 * is needed.
 */
export async function loadOptionsForExistingPatient(
  search,
  loadedOptions,
  {
    page,
    clinicId,
    clinicBrandId,
    patientSearchApiSignature = '/api/secure/mentor/patient/list',
    setAutoFillStuffForNewPatientString = () => {},
    mentorAuthorisedToSaveAndGetPatientsUsingIdb,
    searchOnImportId = false,
    searchOnUhId = false,
  },
) {
  setAutoFillStuffForNewPatientString(search)

  let optionsArray = []
  let hasMore = true
  const isFirstPage = page === 1
  const canUsePatientSearchIdb =
    mentorAuthorisedToSaveAndGetPatientsUsingIdb &&
    !patientSearchApiSignature?.includes('/api/secure/mentor/patient/list')

  if (canUsePatientSearchIdb && isFirstPage && (!loadedOptions || loadedOptions.length === 0)) {
    const patientsFromIdb = await searchPatientsInIdb(search)
    const idbResults = (patientsFromIdb || [])
      .filter((item) => item?.clinicBrandId === clinicBrandId || item?.clinicId === clinicId)
      .map((option) => ({
        ...option,
        id: option?.id,
        name: option?.name || option?.firstName,
      }))

    if (idbResults.length > 0) {
      return {
        options: idbResults,
        hasMore: true,
        additional: {
          page: page + 1,
          clinicId,
          clinicBrandId,
          setAutoFillStuffForNewPatientString,
          patientSearchApiSignature,
          searchOnImportId,
          searchOnUhId,
          mentorAuthorisedToSaveAndGetPatientsUsingIdb: canUsePatientSearchIdb,
        },
      }
    }
  }

  let finalSignatureToUse = patientSearchApiSignature
  if (!finalSignatureToUse?.includes('?')) finalSignatureToUse += '?'
  const prefix = finalSignatureToUse.endsWith('?') ? '' : '&'
  finalSignatureToUse += `${prefix}search=${search}&clinicBrandId=${clinicBrandId}&clinicId=${clinicId}&page=${page}`
  if (searchOnUhId) finalSignatureToUse += `&includeUhId=${searchOnUhId}`
  if (searchOnImportId) finalSignatureToUse += `&includeImportId=${searchOnImportId}`

  const response = await axios.get(`${clinicApiURL}${finalSignatureToUse}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      ...commonHeadersCPWeb,
      'ef-user-country-code': localStorage.getItem('countryCode'),
      'ef-app-language': sessionStorage.getItem('languageCode'),
    },
  })

  if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
    if (canUsePatientSearchIdb) addOrUpdatePatientsInIdb(response.data)
    optionsArray = response.data.map((option) => ({
      ...option,
      id: option?.id,
      name: option?.name || option?.firstName,
    }))
    hasMore = response.data.length >= 10
  } else {
    hasMore = false
  }

  return {
    options: optionsArray,
    hasMore,
    additional: {
      page: page + 1,
      clinicId,
      clinicBrandId,
      setAutoFillStuffForNewPatientString,
      patientSearchApiSignature,
      searchOnImportId,
      searchOnUhId,
      mentorAuthorisedToSaveAndGetPatientsUsingIdb,
    },
  }
}
